import { useState } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm() {
  const { t } = useLanguage();
  const form = t("contact.form");
  const projectOptions = form?.projectOptions || [];

  const [formData, setFormData] = useState({
    name: "", company: "", org_number: "", email: "", phone: "", needs: "", project_size: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/leads`, formData);
      setSuccess(true);
      setFormData({ name: "", company: "", org_number: "", email: "", phone: "", needs: "", project_size: "" });
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 lg:py-48 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-mono text-xs tracking-[0.25em] text-[#00F0FF] mb-4 block">
              {t("contact.overline")}
            </span>
            <h2 className="font-outfit font-bold text-4xl md:text-5xl leading-tight tracking-tighter text-white mb-4">
              {t("contact.title")}
            </h2>
            <p className="text-lg text-neutral-400 font-manrope max-w-lg">
              {t("contact.subtitle")}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            {success ? (
              <div data-testid="contact-success" className="p-10 border border-[#00F0FF]/30 bg-[#00F0FF]/5 rounded-sm text-center">
                <p className="text-[#00F0FF] font-manrope text-lg">{form?.success}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 mb-2 block">{form?.name}</label>
                    <Input
                      data-testid="contact-name"
                      required
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      className="bg-black border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] rounded-sm"
                      placeholder={form?.name}
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 mb-2 block">{form?.company}</label>
                    <Input
                      data-testid="contact-company"
                      required
                      value={formData.company}
                      onChange={(e) => handleChange("company", e.target.value)}
                      className="bg-black border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] rounded-sm"
                      placeholder={form?.company}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 mb-2 block">{form?.orgNumber}</label>
                    <Input
                      data-testid="contact-org-number"
                      value={formData.org_number}
                      onChange={(e) => handleChange("org_number", e.target.value)}
                      className="bg-black border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] rounded-sm"
                      placeholder={form?.orgNumber}
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 mb-2 block">{form?.email}</label>
                    <Input
                      data-testid="contact-email"
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className="bg-black border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] rounded-sm"
                      placeholder={form?.email}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 mb-2 block">{form?.phone}</label>
                    <Input
                      data-testid="contact-phone"
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      className="bg-black border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] rounded-sm"
                      placeholder={form?.phone}
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 mb-2 block">{form?.projectSize}</label>
                    <Select
                      value={formData.project_size}
                      onValueChange={(val) => handleChange("project_size", val)}
                    >
                      <SelectTrigger
                        data-testid="contact-project-size"
                        className="bg-black border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] rounded-sm"
                      >
                        <SelectValue placeholder={form?.projectSize} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        {projectOptions.map((opt, i) => (
                          <SelectItem key={i} value={opt} className="text-white hover:bg-white/10 focus:bg-white/10">
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 mb-2 block">{form?.needs}</label>
                  <Textarea
                    data-testid="contact-needs"
                    value={formData.needs}
                    onChange={(e) => handleChange("needs", e.target.value)}
                    rows={4}
                    className="bg-black border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] rounded-sm resize-none"
                    placeholder={form?.needs}
                  />
                </div>

                {error && <p data-testid="contact-error" className="text-red-500 text-sm">{error}</p>}

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="w-full bg-[#00F0FF] text-black font-medium py-3.5 rounded-sm hover:shadow-[0_0_25px_rgba(0,240,255,0.5)] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {form?.submit}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
